import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Avatar,
  Button,
} from '@mui/material';
import {
  CalendarToday,
  Person,
  ArrowForward,
} from '@mui/icons-material';

const BlogPage: React.FC = () => {
  const blogPosts = [
    {
      title: 'The Future of Government IT: Cloud-First Strategies for 2025',
      excerpt: 'Explore how federal agencies are adopting cloud-first approaches to modernize legacy systems and improve citizen services.',
      author: 'Sarah Johnson',
      date: '2025-09-05',
      category: 'Cloud Computing',
      readTime: '5 min read',
      image: '/api/placeholder/400/250',
    },
    {
      title: 'Zero Trust Architecture: A Complete Guide for Government Contractors',
      excerpt: 'Learn how to implement zero trust security principles to meet federal compliance requirements and protect sensitive data.',
      author: 'Michael Chen',
      date: '2025-09-01',
      category: 'Cybersecurity',
      readTime: '8 min read',
      image: '/api/placeholder/400/250',
    },
    {
      title: 'Supply Chain Resilience: Lessons from Recent Disruptions',
      excerpt: 'Discover strategies for building resilient supply chains that can withstand global disruptions and maintain operational continuity.',
      author: 'Jennifer Williams',
      date: '2025-08-28',
      category: 'Logistics',
      readTime: '6 min read',
      image: '/api/placeholder/400/250',
    },
    {
      title: 'FedRAMP Compliance: Common Pitfalls and How to Avoid Them',
      excerpt: 'Navigate the FedRAMP authorization process successfully by understanding common challenges and best practices.',
      author: 'John Smith',
      date: '2025-08-25',
      category: 'Compliance',
      readTime: '7 min read',
      image: '/api/placeholder/400/250',
    },
    {
      title: 'AI in Logistics: Transforming Distribution Networks',
      excerpt: 'How artificial intelligence is revolutionizing route optimization, demand forecasting, and warehouse operations.',
      author: 'Jennifer Williams',
      date: '2025-08-20',
      category: 'Innovation',
      readTime: '5 min read',
      image: '/api/placeholder/400/250',
    },
    {
      title: 'Agile Development in Government Projects: Best Practices',
      excerpt: 'Implementing agile methodologies in government contracts while maintaining compliance and documentation requirements.',
      author: 'Sarah Johnson',
      date: '2025-08-15',
      category: 'Development',
      readTime: '6 min read',
      image: '/api/placeholder/400/250',
    },
  ];

  const categories = [
    'All Posts',
    'Cloud Computing',
    'Cybersecurity',
    'Logistics',
    'Compliance',
    'Innovation',
    'Development',
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1e3a8f 0%, #3b82f6 100%)',
          color: 'white',
          pt: 12,
          pb: 8,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h1" gutterBottom sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
            Blog & Insights
          </Typography>
          <Typography variant="h5" sx={{ opacity: 0.95 }}>
            Expert perspectives on technology trends, best practices, and industry insights.
          </Typography>
        </Container>
      </Box>

      {/* Categories */}
      <Box sx={{ py: 3, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {categories.map((category) => (
              <Chip
                key={category}
                label={category}
                clickable
                color={category === 'All Posts' ? 'primary' : 'default'}
                sx={{ fontSize: '0.9rem' }}
              />
            ))}
          </Box>
        </Container>
      </Box>

      {/* Blog Posts Grid */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {blogPosts.map((post, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardMedia
                    component="div"
                    sx={{
                      height: 200,
                      bgcolor: 'grey.300',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="h6" color="text.secondary">
                      Blog Image
                    </Typography>
                  </CardMedia>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Chip label={post.category} size="small" color="primary" />
                      <Typography variant="caption" color="text.secondary">
                        {post.readTime}
                      </Typography>
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      {post.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {post.excerpt}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto' }}>
                      <Avatar sx={{ width: 30, height: 30, mr: 1, bgcolor: 'primary.main' }}>
                        {post.author[0]}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="caption">
                          {post.author}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {new Date(post.date).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Button endIcon={<ArrowForward />} size="small">
                        Read More
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Load More */}
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button variant="outlined" size="large">
              Load More Articles
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Newsletter Signup */}
      <Box sx={{ py: 6, bgcolor: 'primary.main', color: 'white' }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom>
            Stay Updated
          </Typography>
          <Typography variant="body1" paragraph>
            Subscribe to our newsletter for the latest insights on government technology and industry trends.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
            <Button
              variant="contained"
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'grey.100',
                }
              }}
            >
              Subscribe Now
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default BlogPage;